import React, { useEffect, useRef, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { DropField } from '../../../elements/general';
import { useTranslation } from 'react-i18next';
import { Spinner } from 'react-bootstrap';
import { maybeTranslate } from '../../sales/utils/helpers';

const Select = ({
                  options = [],
                  value = null,
                  onChange = () => {
                  },
                  placeholder = '',
                  className,
                  toggleClassName,
                  menuClassName,
                  itemClassName,
                  isLoading = false,
                  infoLabel = '',
                  idKey = 'key', // field in option that is considered id
                  labelKey = 'label', // field that is considered a label
                  returnObject = false, // for storage object
                  isSearchable = false,
                  disabled = false
                }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const toggleRef = useRef(null);
  const inputRef = useRef(null);

  const selectedId = (() => {
    if (value == null) return null;
    return (typeof value === 'object') ? String(value.key ?? value?.id ?? '') : String(value);
  })();

  const selected = selectedId ? options.find(opt => String(opt.key) === selectedId) || null : null;

  const getLabel = (opt) => {
    if (!opt) return placeholder ? t(placeholder) : '';
    return typeof opt.label === 'string' ? t(opt.label) : opt.label;
  };

  useEffect(() => {
    if (!open) {
      setSearchValue(selected ? t(selected[labelKey] ?? '') : '');
    }
  }, [selected, labelKey, open, t]);

  const handleToggle = (isOpen) => {
    if (disabled) {
      setOpen(false);
      return;
    }
    setOpen(isOpen);
    if (isOpen) {
      setSearchValue('');
      setTimeout(() => {
        if (inputRef.current) inputRef.current.focus();
      }, 0);
    }
  };

  const handleSelect = (opt) => {
    if (!opt) return;
    onChange(returnObject ? opt : opt.key);
    setOpen(false);
  };

  const filteredOptions = (() => {
    if (!isSearchable) return options;
    const q = (searchValue || '').trim().toLowerCase();
    if (!q) return options;
    return options.filter(opt => {
      const label = String(opt.searchLabel ?? opt[labelKey] ?? '').toLowerCase();
      return label.includes(q);
    });
  })();

  return (
    <DropField className={`${className ?? `custom-dropdown h-100 py-2 rounder-4px border`} ${disabled ? 'mepos-select-disabled' : ''}`}>
      <div style={{ position: 'relative' }}>
        <Dropdown
          drop="down"
          show={open}
          onToggle={handleToggle}
        >
          <Dropdown.Toggle
            className={`me-pos-select-toggle ${toggleClassName ?? 'w-100 d-flex justify-content-between align-items-center'} ${infoLabel ? 'py-1' : ''}`}
            ref={toggleRef}
            disabled={disabled}
          >
            <span
              className={`col text-start text-truncate ${infoLabel ? 'd-flex flex-column' : ''} ${!selected ? 'text-info' : ''}`}
              style={{ visibility: isSearchable && open ? 'hidden' : 'visible' }}
            >
              <small
                className="text-grey text-info"
                style={{ lineHeight: '85%' }}
              >
                {infoLabel && t(infoLabel)}
              </small>
              {getLabel(selected)}
            </span>
            <span className="d-flex align-items-center text-center px-1" style={{ minWidth: '22px' }}>
              {isLoading ? <Spinner animation="border" size="sm" variant={'secondary'} /> :
                <i className={`bi bi-chevron-down transition-transform ${open ? 'rotate-180' : ''}`} />}
            </span>
          </Dropdown.Toggle>
          <Dropdown.Menu className={menuClassName ?? 'custom-dropdown-menu'}>
            {!isLoading && filteredOptions.length === 0 && (<Dropdown.Item disabled className="text-info">
              {t('salesFilters.No options')}
            </Dropdown.Item>)}
            {filteredOptions.map((option, index) => (
              <Dropdown.Item
                key={String(option[idKey] ?? `option-${index}`)}
                onClick={(e) => {

                  e.stopPropagation();
                  if (!option.disabled) handleSelect(option);
                }}
                className={`${itemClassName} ${String(option.key) === selectedId ? 'active' : ''}`}
                disabled={option.disabled}
                title={option[labelKey] ?? option[idKey]}
              >
              {maybeTranslate(option.label)}
            </Dropdown.Item>))}
          </Dropdown.Menu>
        </Dropdown>
        {isSearchable && open && !disabled && (
          <input
            ref={inputRef}
            type="text"
            placeholder={t('salesFilters.Search')}
            className="position-absolute top-0 ou start-0 end-0 bottom-0 w-100 h-100 border-0 bg-transparent ps-3 pe-4 text-reset shadow-none z-2"
            style={{ outline: 'none' }}
            value={searchValue || ''}
            onChange={(e) => {
              const v = e.target.value;
              setSearchValue(v);
              if (!open) setOpen(true);
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              setOpen(prev => {
                const next = !prev;
                if (next) {
                  setSearchValue('');
                  setTimeout(() => {
                    if (inputRef.current) inputRef.current.focus();
                  }, 0);
                }
                return next;
              });
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (!open) setOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const first = filteredOptions[0];
                if (first && !first.disabled) {
                  handleSelect(first);
                }
              }
              if (e.key === 'Escape') {
                setOpen(false);
              }
            }}
          />
        )}
      </div>
    </DropField>
  );
};

export default Select;